import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircle2,
  CircleAlert,
  Info,
  Loader2,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MessageContext } from './message-context';

export type MessageType = 'success' | 'error' | 'info' | 'warning' | 'loading';
export type MessageKey = string | number;

export type MessageOptions = {
  content: ReactNode;
  type?: MessageType;
  key?: MessageKey;
  /** Display time in seconds. Use 0 to keep the message open. */
  duration?: number;
  icon?: ReactNode;
  className?: string;
  onClose?: () => void;
};

type MessageContent = ReactNode | MessageOptions;
export type MessageClose = () => void;

export type MessageApi = {
  open: (options: MessageOptions) => MessageClose;
  success: (content: MessageContent) => MessageClose;
  error: (content: MessageContent) => MessageClose;
  info: (content: MessageContent) => MessageClose;
  warning: (content: MessageContent) => MessageClose;
  loading: (content: MessageContent) => MessageClose;
  destroy: (key?: MessageKey) => void;
};

export type MessageProviderProps = {
  children: ReactNode;
  /** Default display time in seconds. */
  duration?: number;
  maxCount?: number;
  top?: number;
};

type MessageItem = Required<Pick<MessageOptions, 'key' | 'type' | 'content'>> &
  Omit<MessageOptions, 'key' | 'type' | 'content'> & {
    state: 'open' | 'closing';
  };

const MESSAGE_EXIT_DURATION_MS = 200;

const typeStyles: Record<MessageType, string> = {
  success: 'text-emerald-600',
  error: 'text-destructive',
  info: 'text-brand-600',
  warning: 'text-amber-500',
  loading: 'text-brand-600',
};

const typeIcons: Record<MessageType, LucideIcon> = {
  success: CheckCircle2,
  error: CircleAlert,
  info: Info,
  warning: TriangleAlert,
  loading: Loader2,
};

function isMessageOptions(content: MessageContent): content is MessageOptions {
  return (
    typeof content === 'object' &&
    content !== null &&
    !Array.isArray(content) &&
    'content' in content
  );
}

export function MessageProvider({
  children,
  duration = 3,
  maxCount = 5,
  top = 20,
}: MessageProviderProps) {
  const [items, setItems] = useState<MessageItem[]>([]);
  const itemsRef = useRef<MessageItem[]>([]);
  const timersRef = useRef(new Map<MessageKey, number>());
  const exitTimersRef = useRef(new Map<MessageKey, number>());
  const nextKeyRef = useRef(0);

  const updateItems = useCallback((next: MessageItem[]) => {
    itemsRef.current = next;
    setItems(next);
  }, []);

  const close = useCallback(
    (key: MessageKey) => {
      const timer = timersRef.current.get(key);
      if (timer !== undefined) window.clearTimeout(timer);
      timersRef.current.delete(key);

      const item = itemsRef.current.find((candidate) => candidate.key === key);
      if (!item || item.state === 'closing') return;

      updateItems(
        itemsRef.current.map((candidate) =>
          candidate.key === key
            ? { ...candidate, state: 'closing' }
            : candidate,
        ),
      );
      exitTimersRef.current.set(
        key,
        window.setTimeout(() => {
          exitTimersRef.current.delete(key);
          const closingItem = itemsRef.current.find(
            (candidate) => candidate.key === key,
          );
          if (!closingItem || closingItem.state !== 'closing') return;
          updateItems(
            itemsRef.current.filter((candidate) => candidate.key !== key),
          );
          closingItem.onClose?.();
        }, MESSAGE_EXIT_DURATION_MS),
      );
    },
    [updateItems],
  );

  const destroy = useCallback(
    (key?: MessageKey) => {
      if (key !== undefined) {
        close(key);
        return;
      }
      for (const timer of timersRef.current.values())
        window.clearTimeout(timer);
      timersRef.current.clear();
      for (const item of itemsRef.current) close(item.key);
    },
    [close],
  );

  useEffect(
    () => () => {
      for (const timer of timersRef.current.values())
        window.clearTimeout(timer);
      for (const timer of exitTimersRef.current.values())
        window.clearTimeout(timer);
    },
    [],
  );

  const open = useCallback(
    (options: MessageOptions): MessageClose => {
      const key = options.key ?? 'message-' + nextKeyRef.current++;
      const type = options.type ?? 'info';
      const item: MessageItem = {
        key,
        type,
        content: options.content,
        state: 'open',
        ...(options.duration !== undefined
          ? { duration: options.duration }
          : {}),
        ...(options.icon !== undefined ? { icon: options.icon } : {}),
        ...(options.className !== undefined
          ? { className: options.className }
          : {}),
        ...(options.onClose !== undefined ? { onClose: options.onClose } : {}),
      };

      const existingIndex = itemsRef.current.findIndex(
        (candidate) => candidate.key === key,
      );
      const exitTimer = exitTimersRef.current.get(key);
      if (exitTimer !== undefined) window.clearTimeout(exitTimer);
      exitTimersRef.current.delete(key);
      let next =
        existingIndex === -1
          ? [...itemsRef.current, item]
          : itemsRef.current.map((candidate) =>
              candidate.key === key ? item : candidate,
            );

      const openItems = next.filter((candidate) => candidate.state === 'open');
      if (openItems.length > maxCount) {
        const removed = openItems.slice(0, openItems.length - maxCount);
        updateItems(next);
        for (const candidate of removed) close(candidate.key);
        next = itemsRef.current;
      }
      updateItems(next);

      const previousTimer = timersRef.current.get(key);
      if (previousTimer !== undefined) window.clearTimeout(previousTimer);
      const displayDuration = options.duration ?? duration;
      if (displayDuration > 0) {
        timersRef.current.set(
          key,
          window.setTimeout(() => close(key), displayDuration * 1000),
        );
      } else {
        timersRef.current.delete(key);
      }

      return () => close(key);
    },
    [close, duration, maxCount, updateItems],
  );

  const api = useMemo<MessageApi>(() => {
    const typed = (type: MessageType) => (content: MessageContent) =>
      open({
        ...(isMessageOptions(content) ? content : { content }),
        type,
      });

    return {
      open,
      success: typed('success'),
      error: typed('error'),
      info: typed('info'),
      warning: typed('warning'),
      loading: typed('loading'),
      destroy,
    };
  }, [destroy, open]);

  return (
    <MessageContext value={api}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 z-100 flex flex-col items-center gap-3 px-4"
          style={{ top }}
        >
          {items.map((item) => {
            const Icon = typeIcons[item.type];
            return (
              <div
                key={item.key}
                role={item.type === 'error' ? 'alert' : 'status'}
                data-state={item.state}
                className={cn(
                  'pointer-events-auto flex max-w-md items-center gap-3 rounded-xl border border-border/70 bg-popover px-4 py-3 text-sm text-popover-foreground shadow-xl duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2 data-[state=open]:zoom-in-95 data-[state=closing]:animate-out data-[state=closing]:fade-out-0 data-[state=closing]:slide-out-to-top-2 data-[state=closing]:zoom-out-95 data-[state=closing]:pointer-events-none motion-reduce:animate-none',
                  item.className,
                )}
              >
                {item.icon ?? (
                  <Icon
                    className={cn(
                      'size-5 shrink-0',
                      typeStyles[item.type],
                      item.type === 'loading' && 'animate-spin',
                    )}
                    aria-hidden="true"
                  />
                )}
                <span className="min-w-0 flex-1">{item.content}</span>
                <button
                  type="button"
                  aria-label="关闭消息"
                  onClick={() => close(item.key)}
                  className="-mr-1 rounded-sm p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="size-4" aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </MessageContext>
  );
}
